/**
 * this BoughtTogether react component that will render a link from Amplience. This should utilize the existing link_huge
 *
 * @module views/components/BoughtTogether
 * @memberof -Common
 */
import './BoughtTogether.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { Text as PalText } from 'web-palette/dist/components/Text/Text';

import Button from '@ulta/core/components/Button/Button';
import CheckboxToggle from '@ulta/core/components/CheckboxToggle/CheckboxToggle';
import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { partialDatacaptureEventAggregator } from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import MiniProductCard from '@ulta/components/MiniProductCard/MiniProductCard';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './BoughtTogether';

export const ANIMATION_DURATION = 2000;

/**
 * Represents a BoughtTogetherItem component
 *
 * @method
 * @param {BoughtTogetherItem} props - React properties passed from composition
 * @returns BoughtTogetherItem
 */
const BoughtTogetherItem = function( props ){
  const { checked, loading, product, onChange, stepNumber } = props || {};

  if( !product ){
    return null;
  }

  const { action, brandName, price, productId, productName, productPrice, productTag, salePrice, skuId, partialDataCaptureEvent } = product || {};

  const linkAction = productTag?.message ? null : action;
  const id = `BoughtTogetherItem-${skuId}${productId}`;
  const actualPriceFormatted = `$${productPrice?.toFixed( 2 )}`; // While the field `price` is formatted, it does not account for sales. The field productPrice is not formatted, but reflects the true price, including any sales
  const checkboxLabel = `${brandName} ${productName} — ${actualPriceFormatted}`;
  const stepHeading = stepNumber ? `${stepNumber}-heading`.replaceAll( ' ', '' ) : '';
  const productCardComponent = (
    <MiniProductCard
      { ...spreadProps( { ...product } ) }
      htmlTag='h3'
      eyebrow={ productTag }
      action={ null }
      price={ price }
      salePrice={ salePrice }
    />
  );


  return (
    <div
      className='BoughtTogetherItem__step'
      role='group'
      { ...( stepHeading && { 'aria-labelledby': stepHeading } ) }
    >
      { stepNumber && (
        <div className='BoughtTogetherItem__step-label'>
          <PalText
            htmlTag='p'
            textAlign='left'
            textColor='default'
            textStyle='title-xs'
            textBold={ true }
            { ...( stepHeading && { 'id': stepHeading } ) }
          >
            { stepNumber }
          </PalText>
        </div>
      ) }
      <div className='BoughtTogetherItem'>
        <CheckboxToggle
          id={ id }
          name={ id }
          checked={ checked }
          onChange={ ( props ) => {
            onChange?.( { ...props, checked, skuId, productId, productPrice, partialDataCaptureEvent } );
          } }
          disabled={ loading }
        >
          <span className='sr-only'>
            { checkboxLabel }
          </span>
        </CheckboxToggle>
        { linkAction ? (
          <Link_Huge action={ linkAction }
            className='BoughtTogetherItem__link'
          >
            { productCardComponent }
          </Link_Huge>
        ) : productCardComponent }
      </div>
    </div>
  );
};

/**
 * Represents a Footer component
 *
 * @method
 * @param {Footer} props - React properties passed from composition
 * @returns Footer
 */

const RenderFooter = ( {
  isRoutineBuilder,
  breakpoint,
  calculatedSubtotalLabel,
  loading,
  selectedItemsMap,
  formattedAction,
  actionLabel,
  addToBagAction,
  composeOnClick
} ) => (
  <div
    className={ classNames( 'BoughtTogether__footer-wrapper', {
      'BoughtTogether__footer-wrapper--centered': isRoutineBuilder,
      'BoughtTogether__footer-wrapper--left-align': breakpoint?.CURRENT_BREAKPOINT === 'MD'
    } ) }
  >
    <div
      className='BoughtTogether__footer'
      aria-live='polite'
      aria-atomic='true'
    >
      <PalText
        htmlTag='p'
        textStyle='body'
        textColor='subtle'
      >{ calculatedSubtotalLabel }</PalText>
      <div className='BoughtTogether__animation'></div>
      { addToBagAction && (
        <Button
          disabled={ loading || Object.keys( selectedItemsMap ).length < 1 }
          variant={ loading ? 'secondary' : 'primary' }
          size='compact'
          action={ formattedAction }
          label={ actionLabel }
          onClick={ composeOnClick }
          className={ loading && 'BoughtTogether__atc--isLoading' }
        >
          { loading && (
            <Icon aria-hidden={ true }
              size={ 's' }
              name={ 'Check' }
              burst
            />
          ) }
        </Button>
      ) }
    </div>
  </div>
);

/**
 * Represents a BoughtTogether component
 *
 * @method
 * @param {BoughtTogether} props - React properties passed from composition
 * @returns BoughtTogether
 */
export const BoughtTogether = function( props ){
  const {
    title,
    subTitle,
    items,
    subTotal: subTotalLabel,
    invokeMutation,
    root,
    rootMargin,
    threshold,
    addToBagAction,
    setShowSpacer,
    type,
    backgroundColor,
    description
  } = props;

  const isRoutineBuilder = type === 'routineBuilder';

  const [loading, setLoading] = useState( false );
  const [selectedItemsMap, setSelectedItemsMap] = useState( {} );
  const [actionLabel, setActionLabel] = useState( '' );
  const [calculatedSubtotalLabel, setCalculatedSubtotalLabel] = useState( '' );

  const boughtTogetherRef = useRef();
  const { breakpoint } = useDeviceInflection();

  useIntersectionObserver(
    boughtTogetherRef,
    {
      root,
      rootMargin,
      threshold
    },
    handleIntersection( props )
  );

  // Initializes the "checked" state for the checkboxes, and initializes the labels. This data comes in after the component has loaded.
  useEffect( () => {
    const updatedSelectedItemsMap = utils.initializeSelectedItems( {
      items,
      subTotalLabel
    }, { setSelectedItemsMap } );
    utils.getLabels( {
      actionLabel: addToBagAction?.label,
      subTotalLabel,
      selectedItemsMap: updatedSelectedItemsMap
    }, { setActionLabel, setCalculatedSubtotalLabel } );
  }, [items] );

  const formattedAction = useCallback( ( addToBagAction, selectedItemsMap ) => {
    return utils.initializeFormattedAction( { addToBagAction, selectedItemsMap }, { partialDatacaptureEventAggregator } );
  }, [addToBagAction, selectedItemsMap] )( addToBagAction, selectedItemsMap );

  const composeOnClick = useCallback( () => {
    utils.composeOnClick( { formattedAction, loading }, { invokeMutation, setLoading } );
  }, [formattedAction, loading] );

  const handleInputChange = useCallback(
    ( { checked, skuId, productId, productPrice, partialDataCaptureEvent } ) => {
      const updatedSelectedItemsMap = { ...selectedItemsMap };
      if( !checked ){
        updatedSelectedItemsMap[skuId] = {
          productPrice,
          productId,
          skuId,
          partialDataCaptureEvent
        };
      }
      else {
        delete updatedSelectedItemsMap[skuId];
      }
      setSelectedItemsMap( updatedSelectedItemsMap );
      utils.getLabels( {
        actionLabel: addToBagAction?.label,
        subTotalLabel,
        selectedItemsMap: updatedSelectedItemsMap
      }, { setActionLabel, setCalculatedSubtotalLabel } );
    }, [selectedItemsMap]
  );

  if( !hasItems( items ) ){
    setShowSpacer( false );
    return null;
  }

  const footerProps = {
    isRoutineBuilder,
    breakpoint,
    calculatedSubtotalLabel,
    loading,
    selectedItemsMap,
    formattedAction,
    actionLabel,
    addToBagAction,
    composeOnClick
  };

  return (
    <div className='BoughtTogether'>
      <Formik initialValues={ {} }>
        <Form>
          <div
            className={ classNames( 'BoughtTogether__container', {
              [`BoughtTogether--${backgroundColor}`]: isRoutineBuilder && backgroundColor
            } ) }
          >
            { isRoutineBuilder ? (
              <div className='BoughtTogether__header-wrapper'>
                <div className='BoughtTogether__header'>
                  <PalText htmlTag={ title?.htmlTag ?? 'h2' } textStyle='title-lg'>
                    { title.text }
                  </PalText>
                  <PalText htmlTag='p' textStyle='body' textColor='content-default' className='BoughtTogether__subtitle'>
                    { description }
                  </PalText>
                </div>
              </div>
            ) : (
              <div className='BoughtTogether__header'>
                { title && (
                  <Text htmlTag={ title.htmlTag ?? 'h2' }
                    textStyle='title-5'
                  >
                    { title.text }
                  </Text>
                ) }
                { subTitle && (
                  <Text htmlTag='p'
                    textStyle='body-2'
                    color='neutral-600'
                  >
                    { subTitle }
                  </Text>
                ) }
              </div>
            ) }
            <div
              className={ classNames( 'BoughtTogether__product-wrapper', {
                'BoughtTogether__product-wrapper-routine': isRoutineBuilder
              } ) }
            >
              { items?.map( ( product, i ) => (
                <React.Fragment key={ i }>
                  <BoughtTogetherItem
                    product={ product }
                    key={ i }
                    onChange={ handleInputChange }
                    checked={ product?.skuId in selectedItemsMap }
                    stepNumber={ isRoutineBuilder ? items[i].cardTitle : null }
                  />
                  { isRoutineBuilder && i < items.length && (
                    <div className='BoughtTogether__divider' aria-hidden='true'></div>
                  ) }
                </React.Fragment>
              ) ) }
              { isRoutineBuilder && <RenderFooter { ...footerProps } /> }
            </div>
            { !isRoutineBuilder && <RenderFooter { ...footerProps } /> }
          </div>
        </Form>
      </Formik>
    </div>
  );
};

/**
 * Method to handle the click event for primary action
 * @param {object} data - data object passed in as an argument
 * @param {object} data.formattedAction - the action, pre-formatted for a <Button/> action
 * @param {boolean} data.loading - true if the request is loading; otherwise, false
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.invokeMutation- common invoke mutation function
 * @param {function} methods.setLoading - set state when action is in progress
 */
export const composeOnClick = ( data, methods ) => {
  const { formattedAction, loading } = data || {};
  const { invokeMutation, setLoading } = methods || {};

  if( !invokeMutation || !formattedAction ){
    return;
  }
  const { graphql, customHeaders, params, variables } = formattedAction || {};

  const mutation = {
    graphql,
    customHeaders,
    params,
    variables
  };

  if( !loading ){
    setLoading( true );
    setTimeout( () => setLoading( false ), ANIMATION_DURATION );
  }

  invokeMutation( mutation );
};

/**
 * Method to get the action actionLabel to display
 * @param {object} data - data object passed in as an argument
 * @param {string} data.actionLabel - the actionLabel for the action
 * @param {number} data.subTotalLabel - the subTotalLabel actionLabel
 * @param {object} data.selectedItemsMap - map of selected items skuId -> item metadata
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.setActionLabel - setter for actionLabel
 * @param {function} methods.calculatedSubtotalLabel - setter for calculatedSubtotalLabel
 */
export const getLabels = ( data, methods ) => {
  const { actionLabel, subTotalLabel, selectedItemsMap } = data || {};
  const { setActionLabel, setCalculatedSubtotalLabel } = methods || {};

  if( actionLabel && setActionLabel ){
    const count = Object.keys( selectedItemsMap ).length;
    setActionLabel(
      interpolateString( actionLabel, [
        count ? count : ''
      ] )
    );
  }

  if( subTotalLabel && setCalculatedSubtotalLabel ){
    const productsPriceList = Object.values( selectedItemsMap ).map( ( { productPrice } ) => productPrice );
    const productsPriceTotal = productsPriceList.reduce(
      ( sum, price ) => sum + price,
      0
    );
    setCalculatedSubtotalLabel( interpolateString( subTotalLabel, [
      productsPriceTotal.toFixed( 2 ).toLocaleString( 'en' )
    ] ) );
  }
};

/**
 * Method to initialize the action data for the component.
 * @param {object} data - data object passed in as an argument
 * @param {array} data.selectedItemsMap - map of selected items skuId -> item metadata
 */
export const initializeFormattedAction = ( data, methods ) => {
  const { addToBagAction, selectedItemsMap = {} } = data || {};
  const { partialDatacaptureEventAggregator } = methods || {};

  const items = Object.values( selectedItemsMap );

  if( !hasItems( items ) ){
    return {
      ...addToBagAction,
      variables: {
        moduleParams: { products: [] }
      }
    };
  }

  const updatedAction = partialDatacaptureEventAggregator( { action: addToBagAction, selectedItems: items } );
  const products = items.map( ( { productId, skuId } ) => ( { productId, skuIds: [skuId] } ) );

  return {
    ...updatedAction,
    variables: {
      moduleParams: { products }
    }
  };
};

/**
 * Method to initialize the selected items map.
 * @param {object} data - data object passed in as an argument
 * @param {array} data.items - list of relevant items for the Frequently Bought Together section
 * @param {array} data.selectedItemsMap - map of selected items skuId -> item metadata
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.setSelectedItemsMap - setter for selectedItemsMap
 */
export const initializeSelectedItems = ( data, methods ) => {
  const { items } = data || {};
  const { setSelectedItemsMap } = methods || {};

  if( !hasItems( items ) ){
    return {};
  }

  const selectedItemsMap = items.reduce(
    ( map, item ) => {
      const { productPrice, skuId, productId, partialDataCaptureEvent } = item || {};
      const updatedMap = map;
      if( productPrice && skuId && productId && partialDataCaptureEvent ){
        updatedMap[ skuId ] = {
          productPrice,
          productId,
          skuId,
          partialDataCaptureEvent
        };
      }
      return updatedMap;
    },
    {}
  );

  setSelectedItemsMap( selectedItemsMap );
  return selectedItemsMap;
};

/**
 * Property type definitions
 * @typedef BoughtTogether
 * @type {object}
 * @property {object} addToBagAction - Action item for the Add to Bag functionality for the Frequently Bought Together
 * @property {array} items - List of relevant items for the Frequently Bought Together section
 * @property {object} title - Title of the Frequently Bought Together section
 * @property {string} subTitle - subTitle of the Frequently Bought Together section
 * @property {string} subTotalLabel - subTotalLabel label for displaying the cumulative price of the Frequently Bought Together items
 * @property {function} setShowSpacer - Sets the spacer below the component
 */
export const propTypes = {
  addToBagAction: PropTypes.object,
  items: PropTypes.array,
  title: PropTypes.shape( {
    text: PropTypes.string.isRequired,
    htmlTag: PropTypes.string
  } ),
  subTitle: PropTypes.string,
  subTotalLabel: PropTypes.string,
  setShowSpacer: PropTypes.func,
  type: PropTypes.string,
  backgroundColor: PropTypes.string,
  description: PropTypes.string
};

BoughtTogether.propTypes = propTypes;

export default BoughtTogether;
